import React, { useEffect, useState } from 'react';
import { Box, Card, CardMedia, IconButton, Button } from '@mui/material';
import QRCode from 'react-qr-code';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faShareAlt, faGripVertical } from '@fortawesome/free-solid-svg-icons';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ProductDetailsTable1 from './ProductDetailsTable1';
import { FlatButton } from '../pages/commonComponents/FlatButton';
import logoImage from '../images/Indian Design Logo.png';
import { normalizeFabricRecord, updateFabricImage, deleteFabricImage } from '../api';

export const ProductDetails1 = ({ productDetails1, handleEditProduct, handleDeleteProduct, onFabricUpdated }) => {
  const normalizedDetails = normalizeFabricRecord(productDetails1);
  const {
    fabricsup,
    materialcd,
    count,
    construction,
    composition,
    gsm,
    finish,
    cuttable,
    weave,
    price,
    remarks,
    fabrictype,
    moq,
    submitted,
    fpt,
    leadtime,
    uniqueRecordId,
    ID,
    fabric_id,
    imageItems = [],
  } = normalizedDetails || {};

  const [productImages, setProductImages] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    const nextImages = Array.isArray(imageItems) ? imageItems : [];
    const sortedImages = [...nextImages].sort((a, b) => {
      const orderDiff = Number(b.displayOrder || 0) - Number(a.displayOrder || 0);
      if (orderDiff !== 0) return orderDiff;
      return Number(b.id || 0) - Number(a.id || 0);
    });

    setProductImages(sortedImages);
  }, [productDetails1]);

  const displayId = uniqueRecordId || ID || fabric_id || 'FABRIC';
  const qrCodeValue = `${displayId}-${fabricsup || ''}-${materialcd || ''}-${count || ''}-${construction || ''}-${composition || ''}-${gsm || ''}-${fabrictype || ''}`;

  const persistImageOrder = async (orderedImages) => {
    const updates = orderedImages
      .filter((image) => image?.id)
      .map((image, index) =>
        updateFabricImage(image.id, {
          imageUrl: image.url,
          displayOrder: orderedImages.length - index,
        })
      );

    if (!updates.length) return;

    await Promise.all(updates);
    if (typeof onFabricUpdated === 'function') {
      await onFabricUpdated();
    }
  };

  const handleDeleteImage = async (image, index) => {
    const imageId = image?.id;

    if (!imageId) {
      window.alert('Fabric image id is missing.');
      return;
    }

    const isConfirmed = window.confirm('Are you sure you want to delete this fabric image?');
    if (!isConfirmed) {
      return;
    }

    try {
      await deleteFabricImage(imageId);

      const nextImages = productImages.filter((_, currentIndex) => currentIndex !== index);
      setProductImages(nextImages);

      if (nextImages.length > 0) {
        await persistImageOrder(nextImages);
      } else if (typeof onFabricUpdated === 'function') {
        await onFabricUpdated();
      }
    } catch (error) {
      console.error('Error deleting fabric image:', error);
      window.alert(`Error deleting fabric image: ${error.message}`);
    }
  };

  const moveImage = async (dragIndex, hoverIndex) => {
    if (dragIndex === hoverIndex) return;

    const nextImages = [...productImages];
    const [moved] = nextImages.splice(dragIndex, 1);
    nextImages.splice(hoverIndex, 0, moved);
    setProductImages(nextImages);

    try {
      await persistImageOrder(nextImages);
    } catch (error) {
      console.error('Error saving fabric image order:', error);
    }
  };

  return (
    <React.Fragment>
      <Box
        display="flex"
        justifyContent="flex-start"
        sx={{
          bgcolor: 'primary.card',
          paddingTop: 2,
          paddingLeft: 5,
        }}
      >
        <FlatButton label="EDIT" onClick={handleEditProduct} />
      </Box>

      <Box
        id="product-details-container"
        display="flex"
        flexDirection="column"
        sx={{
          bgcolor: '#fff',
          padding: 3,
          minHeight: '80vh',
        }}
      >
        <Box
          sx={{
            width: '100%',
            textAlign: 'center',
            fontSize: 24,
            fontWeight: 600,
            color: '#222',
            mb: 3,
          }}
        >
          {displayId}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <Box sx={{ width: '60%', display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {productImages.length > 0 ? (
              productImages.map((image, index) => (
                <Card
                  key={image.id || image.url || index}
                  raised
                  draggable
                  onDragStart={(event) => {
                    event.dataTransfer.effectAllowed = 'move';
                    event.dataTransfer.setData('text/plain', String(index));
                    setDraggedIndex(index);
                  }}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    const dragIndex = Number(event.dataTransfer.getData('text/plain'));
                    if (!Number.isNaN(dragIndex)) {
                      moveImage(dragIndex, index);
                    }
                    setDraggedIndex(null);
                  }}
                  onDragEnd={() => setDraggedIndex(null)}
                  sx={{
                    width: '48%',
                    minWidth: 280,
                    height: 480,
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 1,
                    border: draggedIndex === index ? '2px solid #1976d2' : '1px solid transparent',
                    opacity: draggedIndex === index ? 0.75 : 1,
                    cursor: 'grab',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      zIndex: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: 'rgba(17, 24, 39, 0.92)',
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    <FontAwesomeIcon icon={faGripVertical} />
                    Drag to reorder
                  </Box>
                  <IconButton
                    onClick={() => handleDeleteImage(image, index)}
                    className="hide-on-screenshot"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 3,
                      color: 'error.main',
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,1)',
                      },
                    }}
                    size="small"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                  <CardMedia
                    component="img"
                    height="480"
                    image={image.url}
                    alt={`${fabricsup || displayId}-${index}`}
                    title={`${fabricsup || displayId}-${index}`}
                    sx={{ padding: '1em', objectFit: 'contain' }}
                  />
                </Card>
              ))
            ) : (
              <Card
                raised
                sx={{
                  width: '48%',
                  minWidth: 280,
                  height: 480,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#9c9c9c',
                }}
              >
                No image found
              </Card>
            )}
          </Box>

          <Box
            sx={{
              width: '40%',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              ml: 3,
            }}
          >
            <ProductDetailsTable1 productDetailsProps={normalizedDetails} />

            <Box sx={{ ml: 3, minWidth: 220 }}>
              <div id="qrCode" style={{ height: '13em' }}>
                <Box sx={{ position: 'relative', width: 200, height: 200 }}>
                  <QRCode size={200} value={qrCodeValue} />
                  <img
                    src={logoImage}
                    alt="Logo"
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '40px',
                      height: '40px',
                    }}
                  />
                </Box>
              </div>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  mt: 2,
                  ml: 5,
                  mr: 4,
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ mb: 2, width: '100%' }}
                  className="hide-on-screenshot"
                >
                  PRINT QR
                </Button>

                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleEditProduct}
                  sx={{ mb: 2, width: '100%' }}
                  className="hide-on-screenshot"
                >
                  EDIT
                </Button>

                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDeleteProduct}
                  sx={{ width: '100%' }}
                  className="hide-on-screenshot"
                >
                  DELETE
                </Button>

                <Box display="flex" sx={{ mt: 2, justifyContent: 'center' }}>
                  <IconButton color="secondary" sx={{ ml: 5 }} className="hide-on-screenshot">
                    <FontAwesomeIcon icon={faDownload} />
                  </IconButton>
                  <IconButton color="primary" sx={{ mr: 2 }} className="hide-on-screenshot">
                    <FontAwesomeIcon icon={faShareAlt} />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </React.Fragment>
  );
};

export default ProductDetails1;
